import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AppLocalStorage } from '../utils/local-storage.util';
import { Profile } from '../session/session.model';
import { SessionService } from '../session/session.service';
 

export interface GreetingData {
  type: 'birthday' | 'anniversary' | 'both';
  name: string;
  messages: string[];
}
 
@Injectable({
  providedIn: 'root'
})
export class GreetingService {
  private profileSubscription: Subscription;
  private greetingSubject = new BehaviorSubject<GreetingData | null>(null);
 
  
  public readonly greeting$ = this.greetingSubject.asObservable();
 
  constructor(private sessionService: SessionService) {
    this.initializeGreetingCheck();
  }
 
  
  private initializeGreetingCheck(): void {
    this.profileSubscription = this.sessionService.userConfigData$.subscribe(
      (userConfig) => {
        if (userConfig && userConfig.profile) {
          // The profile from the stream can be incomplete on initial load.
          const baseProfile = userConfig.profile;
 
          // Manually get the extra profile data (birthDate, dateOfJoining)
          // that SessionService stores separately in local storage.
          const extraProfileData = AppLocalStorage.get('PROFILE_DATA', 'PROFILE_DATA') || {};
 
          // Combine the two parts to create a complete profile for the check.
          const completeProfile: Profile = {
            ...baseProfile,
            ...extraProfileData
          };
          this.checkAndTriggerGreeting(completeProfile);
        }
      }
    );
  }
 
  /**
   * Checks if a birthday or work anniversary greeting should be displayed.
   * @param profile The user's profile data.
   */
  private checkAndTriggerGreeting(profile: Profile): void {
    const today = new Date();
    const todayMonth = today.getMonth(); // 0-indexed
    const todayDay = today.getDate();
    const currentYear = today.getFullYear();
 
    // Use a unique key for today to ensure the greeting is shown only once per day.
    const dateKey = `greeting_shown_${currentYear}-${todayMonth + 1}-${todayDay}`;
    const greetingAlreadyShownToday = AppLocalStorage.get('GREETINGS', dateKey);
 
    if (greetingAlreadyShownToday) {
      return; // A greeting has already been shown today.
    }
 
    let greetings: string[] = [];
    let greetingType: 'birthday' | 'anniversary' | 'both' = null;
 
    // Check for Birthday
    if (profile.birthDate) {
      // Manually parse DD-MM-YYYY format as it's not reliably supported by new Date()
      const parts = profile.birthDate.split('-');
      // new Date() expects (year, monthIndex, day)
      const birthday = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));

      if (!isNaN(birthday.getTime()) && birthday.getMonth() === todayMonth && birthday.getDate() === todayDay) {
        greetings.push(`Happy Birthday, ${profile.employeeName}! We wish you all the best.`);
        greetingType = 'birthday';
      }
    }
 
    // Check for Work Anniversary (regardless of birthday, but not their first year)
    if (profile.dateOfJoining) {
      const joinDate = new Date(profile.dateOfJoining);
      if (joinDate.getMonth() === todayMonth && joinDate.getDate() === todayDay && joinDate.getFullYear() !== currentYear) {
        greetings.push(`Happy Work Anniversary, ${profile.employeeName}! Thank you for your contributions.`);
        if (greetingType === 'birthday') { // If birthday was also found
          greetingType = 'both';
        } else {
          greetingType = 'anniversary';
        }
      }
    }
 
    if (greetings.length > 0) {
      this.greetingSubject.next({ type: greetingType, name: profile.employeeName, messages: greetings });
      // Mark as shown for today in local storage
      AppLocalStorage.set('GREETINGS', dateKey, 'true');
    } else {
      this.greetingSubject.next(null); // No greeting to show
    }
  }
 
  /**
   * Call this method when the pop-up is dismissed by the user.
   * This ensures the `greeting$` observable resets for the next check.
   */
  public dismissGreeting(): void {
    this.greetingSubject.next(null);
  }
 
  ngOnDestroy(): void {
    if (this.profileSubscription) {
      this.profileSubscription.unsubscribe();
    }
  }
}
 